import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'

/**
 * What the reader produced for this conferência, as the object itself.
 *
 * Operators only. A pharmacist reads this screen to ask whether the pharmacy
 * keyed the receita right; this asks whether *we* read it right, which is a
 * different question with a different audience -- and the answer to it is a
 * nested object rather than a sentence. Until now the only way to check the
 * reader was to infer it from what did or did not appear on the board.
 *
 * ## It is the translated shape, and says so
 *
 * The reader's raw answer is kept nowhere: `persistReading` translates before
 * it writes, so the model's `notes`, every preparation's `reasoning`, the dose
 * strings as written and the `cited` wrapper are already gone by the time
 * anything is stored. What this draws is `verifications.prescription` -- the
 * same object scoring compared -- and the banner says so, because somebody
 * debugging the reader against a shape the reader never emitted would be
 * debugging the wrong thing. Keeping the raw reading would mean a column and a
 * migration; see the issue.
 */
export function ReaderObject({
  value,
  label,
  onClose,
}: {
  /** `verifications.prescription`, whatever shape it turned out to have. */
  value: unknown
  /** Which conferência this is, for the dialog's title and its aria label. */
  label: string
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const clear = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(clear)
  }, [copied])

  // `undefined` does not survive JSON.stringify, and a conferência with no
  // reading is a real state rather than an error: it is drawn as one below.
  const text = JSON.stringify(value ?? null, null, 2) ?? 'null'
  const empty = value === null || value === undefined

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(0,9,51,0.35)] p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Objeto do leitor: ${label}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="rounded-base bg-background-raised border-stroke-weak flex max-h-[85vh] w-full max-w-[72rem] flex-col gap-3 overflow-y-auto border p-4"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-tPSemi text-text-strong">Objeto do leitor</h2>
            <span className="text-tSmall text-text-weak">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="tertiary"
              tone="neutral"
              size="small"
              leadingIcon={icons.copy}
              disabled={empty}
              onClick={() => {
                void navigator.clipboard
                  .writeText(text)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false))
              }}
            >
              {copied ? 'Copiado' : 'Copiar JSON'}
            </Button>
            <Button variant="tertiary" tone="neutral" size="small" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>

        {/*
          Said once, at the top, rather than in a tooltip nobody opens. The
          distinction matters precisely to the person who opened this modal.
        */}
        <p className="text-tSmall text-text-weak rounded-base border-stroke-weak bg-fill-weak/40 border p-3">
          Esta é a leitura <strong>já traduzida</strong> — o mesmo objeto que a comparação usou. Não
          é a resposta crua do modelo: o <code>reasoning</code> de cada preparação, as{' '}
          <code>notes</code> e as doses como escritas na receita são descartadas antes de gravar.
        </p>

        {empty ? (
          <p className="text-tP text-text-weak">
            Esta conferência não tem leitura — nenhuma receita foi lida para ela.
          </p>
        ) : (
          <div className="rounded-base border-stroke-weak overflow-x-auto border p-3 font-mono text-[12px] leading-[1.6]">
            <Node value={value} depth={0} />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * How deep the tree opens by default.
 *
 * Two levels shows the preparations and their field names without unrolling
 * every ingredient of a receita with eighteen of them, which is a wall of text
 * rather than a thing to read. Deeper levels are one click away.
 */
const OPEN_TO_DEPTH = 2

function isBranch(value: unknown): value is Record<string, unknown> | unknown[] {
  return typeof value === 'object' && value !== null
}

/** `[3]` or `{5}` — what is inside, without opening it. */
function summarise(value: Record<string, unknown> | unknown[]): string {
  return Array.isArray(value) ? `[${value.length}]` : `{${Object.keys(value).length}}`
}

function entriesOf(value: Record<string, unknown> | unknown[]): [string, unknown][] {
  return Array.isArray(value)
    ? value.map((one, index) => [String(index), one])
    : Object.entries(value)
}

/** One line of the tree: a leaf drawn inline, or a branch that opens. */
function Node({ name, value, depth }: { name?: string; value: unknown; depth: number }) {
  const [open, setOpen] = useState(depth < OPEN_TO_DEPTH)

  if (!isBranch(value)) {
    return (
      <div style={{ paddingLeft: `${depth * 0.9}rem` }}>
        {name !== undefined && <span className="text-text-weak">{name}: </span>}
        <Leaf value={value} />
      </div>
    )
  }

  const entries = entriesOf(value)
  return (
    <div style={{ paddingLeft: `${depth * 0.9}rem` }}>
      <button
        type="button"
        className="hover:text-text-strong text-text-weak flex items-center gap-1 text-left"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <FontAwesomeIcon
          icon={icons.expand}
          className="text-[9px] transition-transform"
          style={{ transform: open ? undefined : 'rotate(-90deg)' }}
        />
        {name !== undefined && <span>{name}</span>}
        <span className="text-text-weak/70">{summarise(value)}</span>
      </button>
      {open &&
        entries.map(([key, child]) => (
          <Node key={key} name={key} value={child} depth={depth + 1} />
        ))}
    </div>
  )
}

/**
 * A value, coloured by what it is.
 *
 * `null` is drawn as itself rather than as an empty space: on a reading it
 * means "the receita does not say", which is a real answer and is not the same
 * as a field nobody filled.
 */
function Leaf({ value }: { value: unknown }) {
  if (value === null) return <span className="text-text-warning">null</span>
  if (typeof value === 'string') return <span className="text-text-strong">"{value}"</span>
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span className="text-text-success tabular-nums">{String(value)}</span>
  }
  return <span className="text-text-weak">{String(value)}</span>
}
